import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarBlank, CaretLeft, CaretRight, ArrowClockwise, Warning, Info } from "@phosphor-icons/react";
import { formatIdDate } from "@/lib/format";

const STATUS_META = {
  belum_termuat: { label: "BELUM", cls: "bg-secondary text-foreground border-border" },
  sudah_termuat: { label: "TERMUAT", cls: "bg-blue-100 text-blue-800 border-blue-500" },
  mismatch: { label: "MISMATCH", cls: "bg-red-100 text-red-800 border-red-500" },
  diverifikasi: { label: "OK", cls: "bg-emerald-100 text-emerald-800 border-emerald-500" },
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(iso, deltaDays) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function busynessMeta(count) {
  if (count === 0) return { cls: "bg-card border-border", label: "Kosong", text: "text-muted-foreground" };
  if (count <= 2) return { cls: "bg-emerald-50 border-emerald-300", label: "Longgar", text: "text-emerald-800" };
  if (count <= 4) return { cls: "bg-amber-50 border-amber-400", label: "Ramai", text: "text-amber-800" };
  return { cls: "bg-red-50 border-red-400", label: "Padat", text: "text-red-800" };
}

function SPBCard({ item }) {
  const meta = STATUS_META[item.status] || { label: item.status?.toUpperCase() || "-", cls: "bg-secondary text-foreground border-border" };
  return (
    <Link
      to={`/spb/${item.id}`}
      className="block border border-border bg-card hover:border-primary hover:bg-primary/5 transition-colors p-2.5"
      data-testid={`cal-spb-${item.no_spb}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-mono font-black text-xs truncate">{item.no_spb}</span>
        <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase ${meta.cls}`}>{meta.label}</span>
      </div>
      <div className="text-[11px] font-mono font-bold text-foreground truncate">{item.no_polisi}</div>
      <div className="text-[10px] text-muted-foreground truncate">
        {item.produk_nama} · {item.jumlah} {item.satuan}
      </div>
      <div className="text-[10px] text-muted-foreground truncate">
        {item.distrik_kode} · {item.nama_supir || "-"}
      </div>
    </Link>
  );
}

export default function SlotCalendar() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/spb/slot-calendar", { params: { tanggal: date } });
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // `api`, `toast`, `formatApiError`, and setters are all stable
    // module-scope refs — only `date` is reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const totalActive = data?.total || 0;
  const busiest = data?.slots?.reduce((best, s) => (s.count > (best?.count || 0) ? s : best), null);

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="border-b border-border pb-6 mb-6">
        <div className="overline">Slot Antrian</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <CalendarBlank size={30} weight="bold" /> Kalender Slot Harian
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Lihat semua SPB per jam antrian. Warna menunjukkan tingkat kesibukan slot supaya admin bisa geser jadwal sebelum antre panjang.
        </p>
      </div>

      {/* Date navigator */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="calendar-nav">
        <Button
          variant="outline"
          className="rounded-none"
          onClick={() => setDate((d) => shiftDate(d, -1))}
          data-testid="cal-prev-day"
        >
          <CaretLeft size={16} />
        </Button>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-none h-11 max-w-[200px] font-mono"
          data-testid="cal-date-input"
        />
        <Button
          variant="outline"
          className="rounded-none"
          onClick={() => setDate((d) => shiftDate(d, 1))}
          data-testid="cal-next-day"
        >
          <CaretRight size={16} />
        </Button>
        <Button
          variant="outline"
          className="rounded-none font-bold"
          onClick={() => setDate(todayISO())}
          data-testid="cal-today"
        >
          Hari Ini
        </Button>
        <Button
          variant="outline"
          className="rounded-none"
          onClick={load}
          data-testid="cal-refresh"
        >
          <ArrowClockwise size={16} />
        </Button>
        <div className="ml-auto text-xs font-mono text-muted-foreground">
          <span className="font-bold text-foreground">{formatIdDate(date)}</span>
          {data && (
            <>
              {" · "}<span className="font-bold text-foreground">{totalActive}</span> SPB aktif
              {busiest && busiest.count > 0 && (
                <>
                  {" · slot tersibuk "}<span className="font-bold text-foreground">{busiest.slot}</span>
                  {" ("}{busiest.count}{")"}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {loading && !data && (
        <div className="p-8 text-center overline text-muted-foreground">Memuat kalender...</div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] font-mono flex-wrap mb-4" data-testid="cal-legend">
        <span className="overline text-muted-foreground">Tingkat Kesibukan:</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-card border border-border inline-block" />Kosong</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-50 border border-emerald-300 inline-block" />Longgar (1-2)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-50 border border-amber-400 inline-block" />Ramai (3-4)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-red-50 border border-red-400 inline-block" />Padat (5+)</span>
      </div>

      {data && (
        <>
          {/* Slot grid */}
          <div className="space-y-3" data-testid="calendar-grid">
            {data.slots.map((s) => {
              const meta = busynessMeta(s.count);
              return (
                <div
                  key={s.slot}
                  className={`border-2 ${meta.cls} p-3`}
                  data-testid={`cal-slot-${s.slot}`}
                >
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="font-mono font-black text-lg tracking-tight">{s.slot}</div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.text}`}>
                        {meta.label}
                      </span>
                    </div>
                    <div className={`text-xs font-mono ${meta.text} font-bold`}>
                      {s.count} SPB
                    </div>
                  </div>
                  {s.items.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">— slot kosong —</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {s.items.map((item) => <SPBCard key={item.id} item={item} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Unscheduled */}
          {data.unscheduled.count > 0 && (
            <div
              className="mt-6 border-2 border-amber-500 bg-amber-50 p-3"
              data-testid="cal-unscheduled"
            >
              <div className="flex items-center gap-2 mb-2">
                <Warning size={16} weight="fill" className="text-amber-600" />
                <div className="font-bold text-sm uppercase tracking-wider text-amber-800">
                  Belum Ada Slot ({data.unscheduled.count})
                </div>
              </div>
              <p className="text-[11px] text-amber-800 mb-2">
                SPB berikut belum diberi jam antrian. Klik untuk edit dan pilih slot.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.unscheduled.items.map((item) => <SPBCard key={item.id} item={item} />)}
              </div>
            </div>
          )}

          {data.total === 0 && (
            <div className="border border-border bg-card p-8 text-center mt-4" data-testid="cal-empty">
              <Info size={24} className="mx-auto text-muted-foreground mb-2" />
              <div className="font-bold">Tidak ada SPB aktif di tanggal ini</div>
              <div className="text-sm text-muted-foreground mt-1">
                Pilih tanggal lain atau buat SPB baru dari menu <Link to="/spb/new" className="underline text-primary">Buat SPB</Link>.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
